/**
 * Copyright (c) [2024] Jangouts
 *
 * This software may be modified and distributed under the terms
 * of the MIT license.  See the LICENSE.txt file for details.
 */

import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import store from "../../state/store";
import { Search, UserPlus, Filter, LogOut } from "react-feather";
import { useToastContext } from "../../components/Toast";
import {
  selectors as callSelectors,
  actionCreators,
} from "../../state/ducks/call";
import { actionCreators as userActionCreators } from "../../state/ducks/user";
import ContactItem from "./ContactItem";
import { apiUserPage } from "../../api/member";
import { apiLogout } from "../../api/Login";
import "./index.css";

function Contacts() {
  const dispatch = useDispatch();
  const contacts = useSelector(callSelectors.getContacts);
  const userInfo = useSelector((state) => state.user.userInfo);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all"); // all, online, offline
  const [loading, setLoading] = useState(false);
  const toast = useToastContext();
  const navigate = useNavigate();

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await apiUserPage({ pageNo: 1, pageSize: 100000 });
      //console.log(response);
      const userList = response.data?.list || [];

      // 将 API 返回的用户数据转换为联系人格式
      const contactsData = userList.map((user) => ({
        id: user.id,
        name: user.nickname || user.username || "未知用户",
        status: user.curStatus,
        lastSeen: new Date().toISOString(),
      }));

      // 更新 Redux store
      dispatch(actionCreators.setContacts(contactsData));
    } catch (error) {
      console.error("获取联系人失败:", error);
    } finally {
      setLoading(false);
    }
  };
  // 加载联系人数据
  useEffect(() => {
    fetchContacts();
  }, [dispatch]);
  // 过滤联系人
  const filteredContacts = contacts.filter((contact) => {
    // 搜索过滤
    const matchesSearch = contact.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    // 状态过滤
    let matchesFilter = true;
    if (filter === "online") {
      matchesFilter = contact.status === "online";
    } else if (filter === "offline") {
      matchesFilter = contact.status === "offline";
    }

    return matchesSearch && matchesFilter;
  });

  // 按状态和名称排序
  const sortedContacts = [...filteredContacts].sort((a, b) => {
    // 在线状态优先
    if (a.status === "online" && b.status !== "online") return -1;
    if (a.status !== "online" && b.status === "online") return 1;

    // 按名称字母顺序
    return a.name.localeCompare(b.name);
  });

  // 统计信息
  const onlineCount = contacts.filter((c) => c.status === "online").length;
  const totalCount = contacts.length;

  const handleAddContact = () => {
    // 这里可以打开添加联系人对话框
    const newContactName = prompt("请输入联系人姓名:");
    if (newContactName && newContactName.trim()) {
      const newContactId = `user_${Date.now()}`;
      // 注意：这里需要调用Redux action来添加联系人
      // dispatch(callActions.addContact({ id: newContactId, name: newContactName.trim(), status: 'offline' }));
      console.log("添加联系人:", newContactName, newContactId);
    }
  };

  // const testSocket = async () => {
  //   const roomIds = [
  //     38194, 30680, 62999, 35416, 48309, 45570, 53805, 42548, 73208,
  //     21725, 69305, 22221, 59362, 24975, 41184, 47634, 49952, 17948, 35134,
  //     44163, 96673, 66073, 67532, 36472, 48528, 60323, 54789, 78967,
  //   ];

  //   roomIds.forEach((roomId) => {
  //     const roomData = {
  //       room: roomId,
  //       permanent: true,
  //     };
  //     const res = janusApi.destroyedRoom(roomData);
  //     console.log("Rooms del====", res);
  //   });
  // };

  const logOut = async () => {
    const res = await apiLogout();
    try {
      if (res.code === 0) {
        toast.success("退出成功！", { duration: 1500 });
        store.dispatch(userActionCreators.resetAuthorized());
        navigate("/login");
      } else {
        toast.error(res.msg, { duration: 1500 });
      }
    } catch (error) {
      toast.error("退出失败，请重试", { duration: 1500 });
    }
  };

  return (
    <>
      <div className="page-contact font-sans text-white">
        {/* 头部 */}
        <div className="header sticky top-0 z-10">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <h1 className="text-xl font-bold">
                  通讯录<span className="text-sm ml-2">({totalCount} 人)</span>
                </h1>
              </div>
              <div className="flex items-center space-x-2">
                <div>{userInfo.nickname}</div>
                <button className="text-white" onClick={logOut}>
                  <LogOut size={22} />
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* 头部 */}

        {/* 搜索栏 */}
        <div className="container mx-auto px-4 py-4">
          <div className="relative mb-2">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {/* <i class="fas fa-search text-blue-300"></i> */}
              <Search size={20} className="text-blue-300" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/20 border-0 focus:ring-2 focus:ring-blue-400 focus:outline-none placeholder-blue-200"
              placeholder="搜索联系人..."
            />
          </div>
        </div>
        {/* 搜索栏 */}
        {/* <button className="text-white" onClick={testSocket}>
          删除
        </button> */}

        {/* 联系人列表 */}
        <div className="overflow-y-auto h-[calc(100vh-180px)]">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
              <p className="text-lg font-medium mb-2">正在加载联系人...</p>
              <p className="text-sm">请稍候</p>
            </div>
          ) : sortedContacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <div className="text-4xl mb-4">👥</div>
              <p className="text-lg font-medium mb-2">未找到联系人</p>
              <p className="text-sm">
                {searchTerm
                  ? `没有找到匹配"${searchTerm}"的联系人`
                  : "联系人列表为空"}
              </p>
            </div>
          ) : (
            <div className="container mx-auto px-4 pb-20">
              <div id="contactList" className="space-y-3">
                {sortedContacts.map((contact) => (
                  <ContactItem key={contact.id} contact={contact} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Contacts;
